/**
 * Curated, regionally-diverse identity + city pool for the LiveActivityFeed
 * social-proof widget. Combined with REAL product names from /api/products at
 * runtime so the notifications surface actual purchasable plans.
 */

export const NAMES = [
  // United States
  "Marcus", "Emily", "Tyrone", "Madison", "DeShawn", "Hannah",
  "Aaliyah", "Brandon", "Jasmine", "Logan", "Brianna",
  // United Kingdom / Ireland
  "Oliver", "Amelia", "Harry", "Charlotte", "Niamh", "Cian", "Saoirse",
  // Western Europe
  "Lucas", "Sophie", "Mathilde", "Lukas", "Elena", "Matteo", "Giulia",
  "Hugo", "Sofia",
  // Eastern Europe
  "Aleksandar", "Petra", "Yana",
  // Australia / NZ
  "Jack", "Chloe", "Ethan", "Ruby", "Liam",
  // Canada
  "Noah", "Olivia", "Liam C.", "Ava",
  // India
  "Aarav", "Priya", "Rohan", "Ananya", "Karthik", "Meera", "Vikram",
  "Lakshmi",
  // China / Hong Kong
  "Wei", "Mei", "Hao", "Ling", "Chun", "Wai",
  // Japan
  "Hiroshi", "Yuki", "Sakura", "Ren",
  // South-East Asia
  "Ahmad", "Siti", "Nurul", "Daniyal",  // Malaysia
  "Budi", "Dewi", "Putri", "Andi",  // Indonesia
  "Minh", "Linh", "Thanh", "Anh",  // Vietnam
  "Jericho", "Mariel", "Joshua", "Andrea", "Christine",  // Philippines
  // West Africa (where AIGL has a strong base)
  "Esther", "Chinedu", "Ifeoma", "Kwame", "Adaeze", "Tunde",
  // Middle East
  "Layla", "Omar", "Fatima", "Yousef",
  // Latin America
  "Isabella", "Diego", "Camila", "Mateo", "Valentina",
];

/**
 * Each city entry: { city: "Lagos", region: "Nigeria" } — region falls back
 * to the country when it's globally recognised.
 */
export const CITIES = [
  // United States — major + secondary metros
  { city: "New York", region: "NY, USA" },
  { city: "Brooklyn", region: "NY, USA" },
  { city: "Los Angeles", region: "CA, USA" },
  { city: "Houston", region: "TX, USA" },
  { city: "Dallas", region: "TX, USA" },
  { city: "Austin", region: "TX, USA" },
  { city: "Atlanta", region: "GA, USA" },
  { city: "Chicago", region: "IL, USA" },
  { city: "Miami", region: "FL, USA" },
  { city: "Orlando", region: "FL, USA" },
  { city: "Philadelphia", region: "PA, USA" },
  { city: "Phoenix", region: "AZ, USA" },
  { city: "Seattle", region: "WA, USA" },
  { city: "Denver", region: "CO, USA" },
  { city: "Boston", region: "MA, USA" },
  { city: "Charlotte", region: "NC, USA" },
  { city: "Detroit", region: "MI, USA" },
  { city: "Las Vegas", region: "NV, USA" },
  { city: "Minneapolis", region: "MN, USA" },
  { city: "Newark", region: "NJ, USA" },
  // Europe
  { city: "London", region: "UK" },
  { city: "Manchester", region: "UK" },
  { city: "Birmingham", region: "UK" },
  { city: "Dublin", region: "Ireland" },
  { city: "Paris", region: "France" },
  { city: "Lyon", region: "France" },
  { city: "Berlin", region: "Germany" },
  { city: "Munich", region: "Germany" },
  { city: "Amsterdam", region: "Netherlands" },
  { city: "Rotterdam", region: "Netherlands" },
  { city: "Madrid", region: "Spain" },
  { city: "Barcelona", region: "Spain" },
  { city: "Rome", region: "Italy" },
  { city: "Milan", region: "Italy" },
  { city: "Lisbon", region: "Portugal" },
  { city: "Zurich", region: "Switzerland" },
  { city: "Stockholm", region: "Sweden" },
  // Australia / NZ
  { city: "Sydney", region: "Australia" },
  { city: "Melbourne", region: "Australia" },
  { city: "Brisbane", region: "Australia" },
  { city: "Perth", region: "Australia" },
  { city: "Auckland", region: "New Zealand" },
  // India
  { city: "Mumbai", region: "India" },
  { city: "Bengaluru", region: "India" },
  { city: "Delhi", region: "India" },
  { city: "Hyderabad", region: "India" },
  { city: "Chennai", region: "India" },
  { city: "Pune", region: "India" },
  { city: "Kolkata", region: "India" },
  { city: "Ahmedabad", region: "India" },
  // China / HK / Taiwan
  { city: "Shanghai", region: "China" },
  { city: "Beijing", region: "China" },
  { city: "Shenzhen", region: "China" },
  { city: "Guangzhou", region: "China" },
  { city: "Chengdu", region: "China" },
  { city: "Hong Kong", region: "Hong Kong" },
  { city: "Kowloon", region: "Hong Kong" },
  { city: "Taipei", region: "Taiwan" },
  // Japan
  { city: "Tokyo", region: "Japan" },
  { city: "Osaka", region: "Japan" },
  { city: "Yokohama", region: "Japan" },
  // South-East Asia
  { city: "Kuala Lumpur", region: "Malaysia" },
  { city: "Penang", region: "Malaysia" },
  { city: "Johor Bahru", region: "Malaysia" },
  { city: "Jakarta", region: "Indonesia" },
  { city: "Surabaya", region: "Indonesia" },
  { city: "Bandung", region: "Indonesia" },
  { city: "Bali", region: "Indonesia" },
  { city: "Ho Chi Minh City", region: "Vietnam" },
  { city: "Hanoi", region: "Vietnam" },
  { city: "Da Nang", region: "Vietnam" },
  { city: "Manila", region: "Philippines" },
  { city: "Cebu", region: "Philippines" },
  { city: "Quezon City", region: "Philippines" },
  { city: "Davao", region: "Philippines" },
  { city: "Singapore", region: "Singapore" },
  { city: "Bangkok", region: "Thailand" },
  // Canada
  { city: "Toronto", region: "ON, Canada" },
  { city: "Vancouver", region: "BC, Canada" },
  { city: "Montréal", region: "QC, Canada" },
  { city: "Calgary", region: "AB, Canada" },
  { city: "Ottawa", region: "ON, Canada" },
  // Africa (AIGL has strong demand)
  { city: "Lagos", region: "Nigeria" },
  { city: "Abuja", region: "Nigeria" },
  { city: "Nairobi", region: "Kenya" },
  { city: "Accra", region: "Ghana" },
  { city: "Johannesburg", region: "South Africa" },
  // Middle East
  { city: "Dubai", region: "UAE" },
  { city: "Abu Dhabi", region: "UAE" },
  { city: "Doha", region: "Qatar" },
  // Latin America
  { city: "São Paulo", region: "Brazil" },
  { city: "Mexico City", region: "Mexico" },
  { city: "Buenos Aires", region: "Argentina" },
];
